import ObjectUtil from '../../util/ObjectUtil';
import JsonUtil from '../../util/JsonUtil';
import DateUtil from '../../util/DateUtil';
import { ErrorService } from './ErrorService';
/**
 * 基类。使我们前端的所有自定义类的基类。
 * 在这个类中可以统计建立的实体个数。
 */
export default class Base {
  private static AUTO_INCREMENT_ID = 0;
  autoId: number = 0;

  // 错误处理器
  private static errorHandler?: (error: Error, context: string, data?: any) => void;

  constructor() {
    this.autoId = Base.generateAutoId();
  }

  static generateAutoId() {
    Base.AUTO_INCREMENT_ID++;
    return Base.AUTO_INCREMENT_ID;
  }

  // 设置全局错误处理器
  static setErrorHandler(handler: (error: Error, context: string, data?: any) => void) {
    Base.errorHandler = handler;
  }

  // 处理错误
  private static handleError(error: Error, context: string, data?: any) {
    console.error(`[Base Error] ${context}:`, error, data);
    
    if (Base.errorHandler) {
      try {
        Base.errorHandler(error, context, data);
      } catch (handlerError) {
        console.error('Error handler itself failed:', handlerError);
      }
    }
  }

  // 安全执行函数
  private static safeExecute<T>(fn: () => T, context: string, defaultValue?: T): T {
    try {
      return fn();
    } catch (error) {
      this.handleError(error as Error, context);
      return defaultValue as T;
    }
  }

  // 把obj中的属性，赋值到this中来。采用深拷贝。
  assign(obj: any) {
    Base.safeExecute(() => {
      ObjectUtil.extend(this, obj);
    }, `assign data to ${this.constructor.name}`, obj);
  }

  /**
   * @param field 字段名
   * @param Clazz 类型名
   */
  assignList<F extends keyof this>(field: string, Clazz: any) {
    Base.safeExecute(() => {
      ObjectUtil.assignList(this, field, Clazz);
    }, `assignList field ${field} in ${this.constructor.name}`);
  }

  /**
   * 根据一个类型，渲染出对应的数组。
   * @param json 字符串或者数组对象。
   * @param Clazz 需要渲染的目标对象
   */
  static renderList(json: any, Clazz: any): any[] {
    return Base.safeExecute(() => {
      let target: any[] = [];
      let arr: any[] = [];

      if (json instanceof String || typeof json === 'string') {
        arr = JsonUtil.parseList(json);
      } else if (json instanceof Array) {
        arr = json;
      } else {
        const error = new Error('源必须为字符或者数组');
        Base.handleError(error, 'renderList', { json, type: typeof json });
        return target;
      }

      // 如果我们要转换成字符串的数组形式，那么this[field]应该是一个字符串才对。
      if (Clazz === String) {
        return arr;
      }

      if (!Clazz || !(Clazz.prototype instanceof Base)) {
        const error = new Error('指定的类型必须是 Base 的子类');
        Base.handleError(error, 'renderList', { Clazz });
        return target;
      }

      for (let i = 0; i < arr.length; i++) {
        try {
          let bean = arr[i];
          let clazz = new Clazz();
          clazz.assign(bean);
          target.push(clazz);
        } catch (error) {
          Base.handleError(error as Error, `renderList item ${i}`, {
            item: arr[i],
            index: i
          });
          // 继续处理下一个元素
          continue;
        }
      }

      return target;
    }, 'renderList', []);
  }

  // 直接render出一个Entity. field字段名，Clazz类名。
  assignEntity(field: any, Clazz: any) {
    Base.safeExecute(() => {
      let thisObj: any = this;
      let obj: any = thisObj[field];
      
      if (!obj) {
        if (Clazz) {
          let EntityClazz: any = this.constructor;
          obj = new EntityClazz()[field];
        } else {
          return;
        }
      }

      if (Clazz === Date) {
        thisObj[field] = DateUtil.str2Date(obj);
      } else if (Clazz.prototype instanceof Base) {
        let bean = new thisObj.constructor()[field];
        if (!bean) {
          bean = new Clazz();
        }

        if (typeof obj === 'string') {
          try {
            obj = JSON.parse(obj);
          } catch (e) {
            const error = new Error('JSON 解析失败');
            Base.handleError(error, 'assignEntity JSON parse', { 
              field, 
              originalValue: obj 
            });
            return;
          }
        }

        if (obj !== null) {
          bean.assign(obj);
          thisObj[field] = bean;
        }
      } else {
        const error = new Error('不支持的 Clazz 类型');
        Base.handleError(error, 'assignEntity', { field, Clazz });
      }
    }, `assignEntity field ${field} in ${this.constructor.name}`);
  }

  // 新增：安全获取属性值
  safeGet<T>(getter: () => T, defaultValue?: T): T {
    return Base.safeExecute(getter, `safeGet in ${this.constructor.name}`, defaultValue);
  }

  // 新增：转换为安全的 JSON
  toSafeJSON(): any {
    return Base.safeExecute(() => {
      return JSON.parse(JSON.stringify(this));
    }, `toSafeJSON in ${this.constructor.name}`, {});
  }

  // 新增：验证对象完整性
  validate(): { isValid: boolean; errors: string[] } {
    return Base.safeExecute(() => {
      const errors: string[] = [];
      
      // 基础验证：检查必要字段
      if (!this.autoId || this.autoId <= 0) {
        errors.push('autoId 无效');
      }

      // 子类可以重写此方法添加更多验证逻辑
      this.customValidate(errors);

      return {
        isValid: errors.length === 0,
        errors
      };
    }, `validate in ${this.constructor.name}`, { isValid: false, errors: ['验证过程出错'] });
  }

  // 子类可以重写的自定义验证方法
  protected customValidate(errors: string[]): void {
    // 默认空实现，子类可以重写
  }
}